'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { StripeEmbeddedCheckout as StripeEmbeddedCheckoutInstance } from '@stripe/stripe-js';
import { getStripeForCheckout } from '@/lib/stripe-client';
import { captureCheckoutError } from '@/lib/posthog-tracking';

type CheckoutState = 'loading' | 'ready' | 'error';
type CheckoutOperation =
  | 'stripe_sdk_load'
  | 'embedded_checkout_initialization'
  | 'embedded_checkout_mount';

interface CheckoutRequest {
  clientSecret: string;
  attempt: number;
}

interface StripeEmbeddedCheckoutProps {
  clientSecret: string;
  refreshClientSecret: () => Promise<string>;
}

// Stripe allows one Embedded Checkout object per page and rejects a second init with
// "You cannot have multiple Embedded Checkout objects." React mounts effects twice in dev,
// and a secret change starts a new init before the old one settles, so inits run one at a
// time: each waits for the previous attempt to mount or be destroyed.
let initQueue: Promise<void> = Promise.resolve();

function destroyCheckout(checkout: StripeEmbeddedCheckoutInstance | null) {
  if (!checkout) return;

  try {
    checkout.destroy();
  } catch {
    // The Stripe instance may already be destroyed during page teardown.
  }
}

export function StripeEmbeddedCheckout({
  clientSecret,
  refreshClientSecret,
}: StripeEmbeddedCheckoutProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const isMountedRef = useRef(true);
  const [checkoutRequest, setCheckoutRequest] = useState<CheckoutRequest>({
    clientSecret,
    attempt: 0,
  });
  const [checkoutState, setCheckoutState] = useState<CheckoutState>('loading');

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setCheckoutRequest((currentRequest) =>
      currentRequest.clientSecret === clientSecret
        ? currentRequest
        : {
            clientSecret,
            attempt: currentRequest.attempt + 1,
          }
    );
  }, [clientSecret]);

  useEffect(() => {
    let disposed = false;
    let embeddedCheckout: StripeEmbeddedCheckoutInstance | null = null;

    setCheckoutState('loading');

    const initializeCheckout = async () => {
      if (disposed) return;
      let operation: CheckoutOperation = 'stripe_sdk_load';

      try {
        const stripe = await getStripeForCheckout();
        operation = 'embedded_checkout_initialization';
        const checkout = await stripe.initEmbeddedCheckout({
          clientSecret: checkoutRequest.clientSecret,
        });

        if (disposed) {
          destroyCheckout(checkout);
          return;
        }

        embeddedCheckout = checkout;
        const container = containerRef.current;
        if (!container) {
          throw new Error('Stripe checkout container is unavailable');
        }

        operation = 'embedded_checkout_mount';
        checkout.mount(container);
        setCheckoutState('ready');
      } catch (error) {
        destroyCheckout(embeddedCheckout);
        embeddedCheckout = null;
        if (!disposed) {
          captureCheckoutError(error, 'payment_processing', { step: operation });
          setCheckoutState('error');
        }
      }
    };

    initQueue = initQueue.then(initializeCheckout);

    return () => {
      disposed = true;
      destroyCheckout(embeddedCheckout);
      embeddedCheckout = null;
    };
  }, [checkoutRequest]);

  const handleRetry = useCallback(async () => {
    setCheckoutState('loading');

    try {
      const nextClientSecret = await refreshClientSecret();
      if (!nextClientSecret) {
        throw new Error('Checkout service did not return a client secret');
      }
      if (isMountedRef.current) {
        setCheckoutRequest((currentRequest) => ({
          clientSecret: nextClientSecret,
          attempt: currentRequest.attempt + 1,
        }));
      }
    } catch {
      if (isMountedRef.current) setCheckoutState('error');
    }
  }, [refreshClientSecret]);

  return (
    <div className="relative min-h-48">
      {/* Stripe's form can take several seconds to draw after it mounts, and its frame
          is transparent until then. The loader stays behind the form through `ready`,
          so the buyer never faces an empty box; the drawn form covers it. */}
      {checkoutState !== 'error' && (
        <div className="absolute inset-x-0 top-0 flex min-h-48 items-center justify-center" role="status">
          <div className="text-center">
            <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-b-[#2A6354]" />
            <p className="text-sm text-gray-600">Loading secure checkout...</p>
          </div>
        </div>
      )}

      {checkoutState === 'error' && (
        <div className="flex min-h-48 items-center justify-center" role="alert">
          <div className="max-w-sm text-center">
            <p className="font-medium text-gray-900">Secure checkout could not load.</p>
            <p className="mt-1 text-sm text-gray-600">Check your connection, then try again.</p>
            <button
              type="button"
              onClick={() => void handleRetry()}
              className="mt-4 rounded-lg bg-[#2A6354] px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
            >
              Retry secure checkout
            </button>
          </div>
        </div>
      )}

      <div ref={containerRef} className="relative z-10" aria-hidden={checkoutState !== 'ready'} />
    </div>
  );
}
